import React, { Dispatch, SetStateAction, createContext, useContext, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ErrorAlert } from 'components/ErrorAlert';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { useNavigate } from 'react-router';
import { NotificationService } from 'services/notification-service';
import { SubscriptionService } from 'services/subscription-service';
import { UserService } from 'services/user-service';
import { EMPTY_SUBSCRIPTION } from 'shared/constants/empty-subscription';
import { SUBSCRIPTION_QUERY_KEY } from 'shared/constants/query-keys';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { Subscription } from 'shared/interfaces/Subscription';

const SubscriptionContext = createContext<[Subscription, Dispatch<SetStateAction<Subscription>>]>([
  EMPTY_SUBSCRIPTION,
  () => {
    // Default Subscription
  }
]);

export function useSubscriptionContext(): [Subscription, Dispatch<SetStateAction<Subscription>>] {
  return useContext(SubscriptionContext);
}

export const SubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
  const [stateSubscription, setStateSubscription] = useState<Subscription>(EMPTY_SUBSCRIPTION);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const subscriptionQuery = useQuery({
    queryKey: [SUBSCRIPTION_QUERY_KEY],
    queryFn: SubscriptionService.getSubscription,
    onSuccess: async (subscription: Subscription) => {
      setStateSubscription(subscription);
    },
    onError: () => {
      UserService.logout();
      navigate(ROUTE_LINKS.login);
      NotificationService.showResult(false, 'AUTH.USER.ERROR');
    }
  });

  const setSubscription: Dispatch<SetStateAction<Subscription>> = (
    newSubscription: Subscription | ((prevSubscription: Subscription) => Subscription)
  ) => {
    queryClient.setQueryData([SUBSCRIPTION_QUERY_KEY], (prevData?: Subscription) => {
      const updatedUser =
        typeof newSubscription === 'function' ? newSubscription(prevData ?? EMPTY_SUBSCRIPTION) : newSubscription;
      return updatedUser === undefined ? prevData : updatedUser;
    });
    setStateSubscription((prevSubscription) => {
      const updatedUser = typeof newSubscription === 'function' ? newSubscription(prevSubscription) : newSubscription;
      return updatedUser === undefined ? prevSubscription : updatedUser;
    });
  };

  if (subscriptionQuery.isError) return <ErrorAlert />;
  if (subscriptionQuery.isLoading || JSON.stringify(stateSubscription) === JSON.stringify(EMPTY_SUBSCRIPTION))
    return <FullScreenLoader />;

  // We do not need to set subscription ever
  return (
    <SubscriptionContext.Provider value={[stateSubscription, setSubscription]}>{children}</SubscriptionContext.Provider>
  );
};
